import { useMutation } from '@apollo/client';
import React, { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useDispatch } from 'react-redux';
import AddIcon from 'mdi-react/AddIcon';
import { toast } from 'react-toastify';
import styled from '../../../../style/styled';
import { setModalType } from '../../../../store/modal/actions';
import { ModalType } from '../../../../store/modal/types';
import { CREATE_TOUR_PLAN_MUTATION } from '../../../../Routes/Account/plans/CreatePlanDialog';
import { Input } from '../../../../styledComponents/Input';
import {
  EXPLORE_WEB_CreateTourPlan as CreatePlanData,
  EXPLORE_WEB_CreateTourPlanVariables as CreatePlanVariables,
} from '../../../../Routes/Account/plans/__generated__/EXPLORE_WEB_CreateTourPlan';
import { ErrorText } from '../../../../styledComponents/ErrorText';
import { SCREEN_WIDTH_BREAK_SMALL } from '../../../../style/SIZES';
import { UNKNOWN_ERROR } from '../../../../consts';
import { captureInSentry } from '../../../config/reporting/captureInSentry';
import { AddIconContainer } from './styledComponents/AddIconContainer';

let _variables: CreatePlanVariables | null = null;

const getVariables = () => _variables;

const setVariables = (variables: CreatePlanVariables) => {
  _variables = variables;
};

interface Props {
  tourID?: string;
  placeID?: string;
}

export const AddItemToNewTourPlan: React.FC<Props> = ({ placeID, tourID }) => {
  const dispatch = useDispatch();

  const [name, setName] = useState<string>('');
  const [error, setError] = useState<Error | null>(null);

  const onSuccess = useCallback(() => {
    dispatch(setModalType({ modalType: ModalType.NONE, modalData: null }));
  }, [dispatch]);

  // setup mutation
  const [createTourPlan, { loading }] = useMutation<
    CreatePlanData,
    CreatePlanVariables
  >(CREATE_TOUR_PLAN_MUTATION, {
    onCompleted: ({ result }) => {
      const { error } = result;

      if (error) {
        const errorMessage = error?.message || UNKNOWN_ERROR;

        captureInSentry(errorMessage, { variables: getVariables() });

        setError(new Error(errorMessage));
      } else {
        onSuccess();
      }
    },
    onError: (error) => {
      Sentry.withScope((scope) => {
        scope.setExtra('variables', getVariables());
        Sentry.captureException(error);
      });

      toast.error('Creating tour plan failed. Please try again later.');
    },
  });

  const handleSubmit = () => {
    if (loading) {
      return;
    }

    if (name.trim().length === 0) {
      setError(new Error('Please specify a name for your tour plan.'));
    } else {
      // call mutation with variables
      const variables: CreatePlanVariables = { input: { name } };

      if (tourID) {
        variables.input.tourIDs = [tourID];
      }

      if (placeID) {
        variables.input.placeIDs = [placeID];
      }

      setVariables(variables);

      createTourPlan({ variables });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setName(e.target.value);
  };

  return (
    <>
      <NewPlanContainer>
        <InputContainer>
          <Input
            id="name"
            name="name"
            type="text"
            // disabled={loading}
            value={name}
            onChange={handleInputChange}
            maxLength={20}
            data-testid="name-input-field"
            placeholder="Enter a name for the tour plan"
          />
        </InputContainer>

        <AddIconContainer>
          <AddIcon onClick={handleSubmit} />
        </AddIconContainer>
      </NewPlanContainer>
      <ErrorText>{error ? error.message : ''}</ErrorText>
    </>
  );
};

const InputContainer = styled.div`
  margin-right: 8px;
  flex-grow: 1;
`;

const NewPlanContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 8px;
`;

export const Section = styled.section`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const Container = styled.div`
  padding: 8px;
  margin: 0 auto;
  width: 300px;

  @media only screen and (max-width: ${SCREEN_WIDTH_BREAK_SMALL}px) {
    width: 95%;
  }
`;

export const Title = styled.h4`
  text-transform: uppercase;
  font-weight: bold;
  margin-bottom: 20px;
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;
